import { useEffect, useRef, useCallback } from "react";

/**
 * Custom hook to manage session timeout based on wall-clock time
 * @param timeoutMinutes - Number of minutes before session times out
 * @param onTimeout - Callback function to execute when session times out
 */
export function useSessionTimeout(
  timeoutMinutes: number,
  onTimeout: () => void
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  const expiryTimeRef = useRef<number>(0);
  const hasExpiredRef = useRef<boolean>(false);

  // Keep the onTimeout ref up to date
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const triggerTimeout = useCallback(() => {
    if (!hasExpiredRef.current) {
      hasExpiredRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      onTimeoutRef.current();
    }
  }, []);

  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset expired flag when starting a new timer
    hasExpiredRef.current = false;

    // Set expiry time based on wall-clock time
    const timeoutMs = timeoutMinutes * 60 * 1000;
    expiryTimeRef.current = Date.now() + timeoutMs;

    // Set up the timeout
    timeoutRef.current = setTimeout(() => {
      triggerTimeout();
    }, timeoutMs);
  }, [timeoutMinutes, triggerTimeout]);

  const checkExpiry = useCallback(() => {
    // Check if session has expired based on wall-clock time
    if (
      !hasExpiredRef.current &&
      expiryTimeRef.current &&
      Date.now() >= expiryTimeRef.current
    ) {
      triggerTimeout();
    }
  }, [triggerTimeout]);

  // Start timer on mount and when timeoutMinutes changes
  useEffect(() => {
    startTimer();

    // Handle page visibility changes (wake from sleep, tab switching, etc.)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkExpiry();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [startTimer, checkExpiry]);

  // Function to reset the timer
  const resetTimer = useCallback(() => {
    startTimer();
  }, [startTimer]);

  return { resetTimer };
}
